/**
 * The rows of a table as stacked cards, for a phone (360 simulation P29).
 *
 * Takes the same column definitions as the `DataGrid`, so a card shows exactly what a
 * row shows, with nothing scrolled sideways: the `lead` columns in full width at the top
 * (no label), then every other column as "Header: value". Columns in `skip` are left
 * out. Columns without a header (selection, actions) go to the foot of the card.
 */

import { Card, type DataGridColumnDef } from "@norican/react";
import type { ReactNode } from "react";

interface LeafColumn<Row> {
  id: string;
  header: ReactNode;
  cell?: (row: Row) => ReactNode;
  accessor?: (row: Row) => unknown;
}

function leaves<Row>(columns: readonly DataGridColumnDef<Row>[]): LeafColumn<Row>[] {
  const out: LeafColumn<Row>[] = [];
  for (const column of columns) {
    if ("columns" in column && Array.isArray(column.columns)) {
      out.push(...leaves(column.columns as DataGridColumnDef<Row>[]));
    } else {
      out.push(column as LeafColumn<Row>);
    }
  }
  return out;
}

function valueOf<Row>(column: LeafColumn<Row>, row: Row): ReactNode {
  if (column.cell) {
    return column.cell(row);
  }
  const value = column.accessor?.(row);
  return value == null ? "-" : String(value);
}

export function ColumnCards<Row>({
  columns,
  data,
  getRowId,
  lead = [],
  skip = [],
  label,
}: {
  columns: readonly DataGridColumnDef<Row>[];
  data: readonly Row[];
  getRowId: (row: Row) => string;
  /** Columns shown first, full width and without their header. */
  lead?: readonly string[];
  /** Columns left out of the cards. */
  skip?: readonly string[];
  label: string;
}) {
  const all = leaves(columns).filter((column) => !skip.includes(column.id));
  const top = lead
    .map((id) => all.find((column) => column.id === id))
    .filter((column): column is LeafColumn<Row> => column !== undefined);
  const rest = all.filter((column) => !lead.includes(column.id));
  const labelled = rest.filter((column) => column.header !== "" && column.header != null);
  const foot = rest.filter((column) => column.header === "" || column.header == null);
  return (
    <ul className="flex flex-col gap-2 p-2" aria-label={label} data-layout="cards">
      {data.map((row) => (
        <li key={getRowId(row)}>
          <Card variant="outline" padding="sm" className="flex min-w-0 flex-col gap-2">
            {top.map((column) => (
              <div key={column.id} className="min-w-0">
                {valueOf(column, row)}
              </div>
            ))}
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1">
              {labelled.map((column) => (
                <div key={column.id} className="contents">
                  <dt className="text-fg-subtle pt-0.5 text-xs">{column.header}</dt>
                  <dd className="min-w-0">{valueOf(column, row)}</dd>
                </div>
              ))}
            </dl>
            {foot.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {foot.map((column) => (
                  <span key={column.id}>{valueOf(column, row)}</span>
                ))}
              </div>
            )}
          </Card>
        </li>
      ))}
    </ul>
  );
}
